import { useCallback, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import AccountDetailsFlyout from '@/components/flyouts/AccountDetailsFlyout';
import { useGetAccountsQuery } from '@/store/accountsApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import type { AccountBase } from '@/models/AccountViewModel';

/**
 * Shared "click an Account Name -> open the Account Details flyout" behavior so it
 * is consistent across grids (EUD-440).
 *
 * The flyout needs a full `AccountBase`, but most grids' rows carry only an account
 * id (`AccountId` on holdings/market-pricing rows, `WealthAccountId` on transaction/
 * gains rows — the same numeric value, see AddTransactionModal where
 * `WealthAccountId` is set from `account.AccountId`). This hook resolves that id to
 * the account object via the accounts list and owns the flyout state, so a grid only
 * needs to: call `openAccountById(row.<accountIdField>)` on click, and render
 * `accountFlyout`.
 */
export function useAccountDetailsFlyout(options?: {
  /** Scope the account lookup to a specific investor. Advisor/client-scoped
   * views fetch their rows with an InvestorId; the lookup must match it or the
   * row account ids won't resolve against the default (advisor's) account list. */
  investorId?: number;
  /** Match the caller's primary-investor scope. Household views (e.g. the PFS)
   * render secondary/household accounts via `OnlyPrimaryInvestors: false`; the
   * lookup must use the same scope or those rows' account ids won't resolve and
   * the account-name link would be disabled for a visibly-rendered account. */
  onlyPrimaryInvestors?: boolean;
  /** Which tab-settings panel the flyout should use (e.g. 'advisor-account').
   * Advisor-context callers pass 'advisor-account' to get the advisor tabs
   * rather than the default investor tabs. */
  tabsPanelKey?: string;
}) {
  const authLoaded = useAuthIsLoaded();
  const investorId = options?.investorId;
  const onlyPrimaryInvestors = options?.onlyPrimaryInvestors;
  // `showClosed` so a row pointing at a since-closed account still resolves; the
  // viewing-tag filter is merged in automatically by the accounts API.
  const { data: accountsData } = useGetAccountsQuery(
    authLoaded
      ? {
          showClosed: true,
          ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
          ...(typeof onlyPrimaryInvestors === 'boolean'
            ? { OnlyPrimaryInvestors: onlyPrimaryInvestors }
            : {}),
        }
      : skipToken,
  );

  const [selectedAccount, setSelectedAccount] = useState<AccountBase | null>(
    null,
  );

  // `useGetAccountsQuery`'s data is loosely typed (any) at this call site; cast
  // to its real response shape so the records are typed AccountBase. Index by id
  // (most rows carry an account id) and by lowercased name (grouped grids hide
  // the id column and only expose the account name as the group key).
  const { accountsById, accountsByName } = useMemo(() => {
    const byId = new Map<number, AccountBase>();
    const byName = new Map<string, AccountBase>();
    const records = accountsData?.Records ?? [];
    // Account names are not guaranteed unique (e.g. two "Checking" accounts).
    // Track collisions and drop them so a by-name lookup (used for grouped
    // group-header rows, which carry no id) never opens an arbitrary account.
    const ambiguousNames = new Set<string>();
    records.forEach((account) => {
      byId.set(account.AccountId, account);
      const name =
        typeof account.Name === 'string'
          ? account.Name.trim().toLowerCase()
          : '';
      if (name === '') return;
      if (byName.has(name)) {
        ambiguousNames.add(name);
      } else {
        byName.set(name, account);
      }
    });
    ambiguousNames.forEach((name) => byName.delete(name));
    return { accountsById: byId, accountsByName: byName };
  }, [accountsData]);

  const openAccountById = useCallback(
    (accountId: number | string | null | undefined) => {
      if (accountId == null || accountId === '') return;
      const account = accountsById.get(Number(accountId));
      if (account) {
        setSelectedAccount(account);
      }
    },
    [accountsById],
  );

  const closeAccountFlyout = useCallback(() => {
    setSelectedAccount(null);
  }, []);

  /** True when the given account id resolves to a real account (use to gate the cursor/click affordance). */
  const canOpenAccount = useCallback(
    (accountId: number | string | null | undefined) =>
      accountId != null &&
      accountId !== '' &&
      accountsById.has(Number(accountId)),
    [accountsById],
  );

  /** Open by account name — for grouped grids whose group key is the account
   * name rather than an id. */
  const openAccountByName = useCallback(
    (name: string | null | undefined) => {
      const key = name?.trim().toLowerCase();
      if (!key) return;
      const account = accountsByName.get(key);
      if (account) {
        setSelectedAccount(account);
      }
    },
    [accountsByName],
  );

  const canOpenAccountByName = useCallback(
    (name: string | null | undefined) => {
      const key = name?.trim().toLowerCase();
      return key != null && key !== '' && accountsByName.has(key);
    },
    [accountsByName],
  );

  const getAccountById = useCallback(
    (accountId: number | string | null | undefined) => {
      if (accountId == null || accountId === '') return undefined;
      return accountsById.get(Number(accountId));
    },
    [accountsById],
  );

  const accountFlyout = selectedAccount ? (
    <AccountDetailsFlyout
      open
      toggleDrawer={closeAccountFlyout}
      account={selectedAccount}
      tabsPanelKey={options?.tabsPanelKey}
      // Fall back to the account's own investor when the caller has no page-level
      // scope (book-of-business / group drilldowns), matching the Accounts page,
      // so the flyout's tab queries are scoped to the row rather than unscoped.
      investorId={investorId ?? selectedAccount.Investors?.[0]?.InvestorId}
    />
  ) : null;

  return {
    openAccountById,
    openAccountByName,
    closeAccountFlyout,
    canOpenAccount,
    canOpenAccountByName,
    getAccountById,
    accountFlyout,
    selectedAccount,
  };
}

export default useAccountDetailsFlyout;
